import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/services/api";
import type { FaqEntry, MoveDirection } from "@/types";

/**
 * React Query hooks for the admin FAQ feature (KC-459). Copies the Sources
 * template: a key factory, one list query, and mutations that invalidate the
 * whole slice on success. The list is small and unpaginated — reordering
 * needs the full ordered list anyway.
 */

export const faqKeys = {
  all: ["admin", "faqs"] as const,
};

export function useAdminFaqs() {
  return useQuery<FaqEntry[]>({
    queryKey: faqKeys.all,
    queryFn: () => api.getAdminFaqs(),
  });
}

function useInvalidateFaqs() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: faqKeys.all });
}

export function useCreateFaq() {
  const invalidate = useInvalidateFaqs();
  return useMutation({
    mutationFn: (data: { question: string; answer: string; is_published: boolean }) =>
      api.createFaq(data),
    onSuccess: invalidate,
  });
}

export function useUpdateFaq() {
  const invalidate = useInvalidateFaqs();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<FaqEntry> }) => api.updateFaq(id, data),
    onSuccess: invalidate,
  });
}

export function useDeleteFaq() {
  const invalidate = useInvalidateFaqs();
  return useMutation({
    mutationFn: (id: string) => api.deleteFaq(id),
    onSuccess: invalidate,
  });
}

export function useMoveFaq() {
  const invalidate = useInvalidateFaqs();
  return useMutation({
    mutationFn: ({ id, direction }: { id: string; direction: MoveDirection }) =>
      api.moveFaq(id, direction),
    onSuccess: invalidate,
  });
}
